import { useUiStore } from '../../stores/ui/store';
import { useCoreStore } from 'src/stores/core/store';
import { AppTableColumnSelection, IDynamicFilterItem, UserPreference } from 'src/stores/core/models';
import { IFilterItem, defaultPagination, ClientSidePaginationInterface, Pagination } from '../../stores/core/models';

const getUserPreferenceValue = (preferenceKey: string, defaultValue: string | undefined = undefined): string => {
  const coreStore = useCoreStore();

  // TODO: use only a getter in store instead this function
  const preferenceValue = coreStore.getUserPreferenceByKey(preferenceKey).value;
  return preferenceValue || defaultValue || '';
};

const getPaginationFromUserPreferences = (key: string | undefined): Pagination | undefined => {
  const coreStore = useCoreStore();
  const paginationUserPreferenceValue = coreStore.getUserPreferenceByKey(key).value;
  return paginationUserPreferenceValue ? (JSON.parse(paginationUserPreferenceValue) as Pagination) : undefined;
};

const getPaginationBasedOnUserPreferences = (key: string): Pagination | undefined => {
  return getPaginationFromUserPreferences(key) || defaultPagination;
};

const saveUserPreference = (userPreference: UserPreference, useSilentMode = false) => {
  const uiStore = useUiStore();
  const coreStore = useCoreStore();

  if (useSilentMode) coreStore.useSilentApiCall = true;

  return uiStore
    .saveUserPreference({
      preferenceKey: userPreference.preferenceKey,
      preferenceValue: userPreference.preferenceValue,
    })
    .finally(() => {
      if (useSilentMode) coreStore.useSilentApiCall = false;
    });
};

const dispatchUpdatePaginationUserPreferenceIfNeeded = (
  preferenceKey: string,
  pagination: Pagination | ClientSidePaginationInterface
): void => {
  const currentPagination = getPaginationFromUserPreferences(preferenceKey);

  if (
    currentPagination === undefined ||
    currentPagination.sortBy !== pagination.sortBy ||
    currentPagination.descending !== pagination.descending ||
    currentPagination.rowsPerPage !== pagination.rowsPerPage
  ) {
    saveUserPreference({
      preferenceKey: preferenceKey,
        preferenceValue: JSON.stringify({ ...pagination, skipActualPage: false }),
    });
  }
};

const getDynamicFiltersUserPreferences = (key: string): IDynamicFilterItem[] | undefined => {
  if (!key) return undefined;
  const userPreferenceValue = getUserPreferenceValue(key) as string;

  if (userPreferenceValue) {
    const dynamicFitlers = JSON.parse(userPreferenceValue) as IDynamicFilterItem[];
    if (dynamicFitlers?.length) return dynamicFitlers;
  }
  return undefined;
};

const getFiltersFromUserPreferences = (key: string | undefined): IFilterItem[] => {
  if (!key) return [];

  const userPreferenceValue = getUserPreferenceValue(key) as string;

  if (userPreferenceValue) {
    const userPreferenceFilters = JSON.parse(userPreferenceValue) as IFilterItem[];
    if (userPreferenceFilters?.length) return userPreferenceFilters;
  }

  return [];
};

const getTableColumnsFromUserPreferences = (key: string) => {
  const value = getUserPreferenceValue(key) as string;
  if (value) return (JSON.parse(value) as AppTableColumnSelection[]) || [];
  return [];
};

export {
  saveUserPreference,
  getUserPreferenceValue,
  getFiltersFromUserPreferences,
  getDynamicFiltersUserPreferences,
  getTableColumnsFromUserPreferences,
  getPaginationBasedOnUserPreferences,
  dispatchUpdatePaginationUserPreferenceIfNeeded,
};
